import React, { useMemo } from "react";
import { Moment } from "moment";
import i18n from "../../../../../i18n/i18n";
import ActionModal, {
  ActionModalConfirmPayload,
  defaultReasonOptions,
} from "./components/ActionModal";
import {
  buildDisableMetadata,
  clampMetadataToMinScheduledUntil,
  resolveReasonKey,
  scheduledUntilToMoment,
  resolveInitialDuration,
} from "./lib/serviceDisableModalUtils";
import {
  DisableDurationPreset,
  IScheduledServiceDisable,
  IServiceDisableMetadata,
} from "../../../../../types/audit";

const serviceTypeToI18nKey: Record<string, string> = {
  pickup: "pickup",
  delivery: "delivery",
  drive_thru: "driveThru",
  offline_loyalty: "offlineLoyalty",
  online_loyalty: "onlineLoyalty",
};

const reasonI18nKeys: Record<string, string> = {
  pos_issue: "reasonPosIssue",
  staff_shortage: "reasonStaffShortage",
  ramadan_hours: "reasonRamadanHours",
  maintenance: "reasonMaintenance",
  brand_request: "reasonBrandRequest",
  other: "reasonOther",
};

export type { ActionModalConfirmPayload } from "./components/ActionModal";
export { defaultReasonOptions } from "./components/ActionModal";
export { default as ActionModal } from "./components/ActionModal";
export type { ActionModalReasonOption } from "./lib/serviceDisableModalUtils";
export {
  buildDisableMetadata,
  resolveReasonKey,
  validateActionModalForm,
  resolveDisableConfirmBehavior,
  scheduledUntilToMoment,
  resolveInitialDuration,
  formatCustomDate,
  DURATION_PRESETS,
  clampMetadataToMinScheduledUntil,
  isDriveThruScheduleBeforePickup,
  isDurationBeforeMinDate,
  resolveScheduledUntilMs,
} from "./lib/serviceDisableModalUtils";

export type ServiceDisableModalMode = "disable" | "scheduled_edit";

export interface ServiceDisableModalConfirmPayload extends ActionModalConfirmPayload {
  metadata: IServiceDisableMetadata;
}

const ServiceDisableModal: React.FC<{
  visible: boolean;
  mode?: ServiceDisableModalMode;
  branchName?: string;
  serviceType?: string;
  initialValues?: IScheduledServiceDisable | null;
  warningText?: string;
  minDate?: Moment | null;
  minDateError?: string;
  onCancel: () => void;
  onConfirm: (payload: ServiceDisableModalConfirmPayload) => void;
  onEnable?: () => void;
}> = ({
  visible,
  mode,
  branchName,
  serviceType,
  initialValues,
  warningText,
  minDate,
  minDateError,
  onCancel,
  onConfirm,
  onEnable,
}) => {
    const isEdit = mode === "scheduled_edit";
    const translatedServiceName = serviceType
      ? i18n.t(`serviceDisableModal:${serviceTypeToI18nKey[serviceType] || serviceType}`)
      : "";
    const translatedReasonOptions = React.useMemo(
      () =>
        defaultReasonOptions.map((opt) => ({
          ...opt,
          label: i18n.t(`serviceDisableModal:${reasonI18nKeys[opt.key] || opt.key}`),
        })),
      [i18n.language]
    );

    const title = isEdit
      ? i18n.t("serviceDisableModal:scheduledDisableTitle")
      : i18n.t("serviceDisableModal:disableServiceTitle", {
        service: translatedServiceName,
        branch: branchName || "",
      });

    const subtitle = isEdit
      ? i18n.t("serviceDisableModal:scheduledDisableSubtitle", { branch: branchName || "" })
      : i18n.t("serviceDisableModal:disableServiceSubtitle");

    const initialReason = useMemo(
      () => resolveReasonKey(initialValues?.reason, translatedReasonOptions),
      [initialValues?.reason, translatedReasonOptions]
    );

    const initialCustomDate = useMemo(
      () => scheduledUntilToMoment(initialValues?.scheduledUntil),
      [initialValues?.scheduledUntil]
    );

    const initialDuration = useMemo(
      () => resolveInitialDuration(initialValues?.duration, initialValues?.scheduledUntil),
      [initialValues?.duration, initialValues?.scheduledUntil]
    );

    const handleConfirm = (payload: ActionModalConfirmPayload) => {
      const duration = (payload.duration || "permanent") as DisableDurationPreset;
      const metadata = clampMetadataToMinScheduledUntil(
        buildDisableMetadata(
          payload.reason,
          duration,
          payload.customDate,
          isEdit && initialValues
            ? {
              duration: initialDuration,
              customDate: initialCustomDate,
              scheduledUntil: initialValues.scheduledUntil,
            }
            : null
        ),
        minDate?.valueOf()
      );

      onConfirm({ ...payload, metadata });
    };

    const durationHint = translatedServiceName
      ? i18n.t("serviceDisableModal:autoResumeLabel", { service: translatedServiceName })
      : undefined;

    return (
      <ActionModal
        visible={visible}
        title={title}
        subtitle={subtitle}
        showDuration={true}
        durationHint={durationHint}
        resumeServiceName={translatedServiceName || undefined}
        reasonOptions={translatedReasonOptions}
        durationLabel={i18n.t("serviceDisableModal:durationLabel")}
        reasonLabel={i18n.t("serviceDisableModal:reasonLabel")}
        reasonPlaceholder={i18n.t("serviceDisableModal:reasonSelectPlaceholder")}
        specifyPlaceholder={i18n.t("serviceDisableModal:specifyReasonPlaceholder")}
        reasonRequiredError={i18n.t("serviceDisableModal:reasonRequired")}
        customDateRequiredError={i18n.t("serviceDisableModal:customDateRequired")}
        initialReasonKey={initialReason.reasonKey}
        initialCustomReasonText={initialReason.customReasonText}
        initialDuration={initialDuration}
        initialCustomDate={initialCustomDate}
        confirmText={isEdit
          ? i18n.t("serviceDisableModal:updateScheduled")
          : i18n.t("serviceDisableModal:save")}
        cancelText={i18n.t("serviceDisableModal:cancel")}
        extraFooterButton={isEdit && onEnable
          ? { text: i18n.t("serviceDisableModal:enable"), onClick: onEnable }
          : undefined}
        warningText={warningText}
        minDate={minDate}
        minDateError={minDateError}
        onCancel={onCancel}
        onConfirm={handleConfirm}
      />
    );
  };

export default ServiceDisableModal;
